import { Close } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import { DIALOG_MAX_WIDTHS } from "src/Components/Common/Modal/ModalWidth";
import { TagsInputUniqueKeys } from "../../ReconMIS/AutoCompletes";

import { FixedSizeList, ListChildComponentProps } from "react-window";
import { VendorDisputeStatusEnumDR } from "./constants/disputeEntries";

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const option = data[index];

  const mergedStyle = {
    ...option.props.style, // ✅ preserves color/fontWeight from renderOption
    ...style, // ✅ includes positioning from react-window
    top: (style.top as number) + LISTBOX_PADDING,
  };

  return React.cloneElement(option, {
    ...option.props,
    style: mergedStyle,
  });
}

export const VirtualizedListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const items = React.Children.toArray(children);
    const ITEM_SIZE = 40;
    const height = Math.min(8, items.length) * ITEM_SIZE + 2 * LISTBOX_PADDING;
    const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => <div ref={ref} {...props} {...other} />);
    OuterElementType.displayName = "OuterElementType";
    return (
      <div ref={ref}>
        <FixedSizeList
          height={height}
          itemCount={items.length}
          itemSize={ITEM_SIZE}
          width="100%"
          itemData={items}
          outerElementType={OuterElementType}
          innerElementType="ul"
        >
          {renderRow}
        </FixedSizeList>
      </div>
    );
  }
);

export function useDebounce<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer); // cleanup on new keystroke
  }, [value, delay]);

  return debouncedValue;
}

const EXCLUDE = "Exclude";

const FilterOfDisputedEntries = ({
  openFilterOfDisputedEntries,
  setOpenFilterOfDisputedEntries,
  filters,
  setFilters,
  data,
}) => {
  const getFilterValue = (filters: any[], id: string): string[] => {
    const filter = filters.find((f) => f.id === id);
    return Array.isArray(filter?.value) ? filter.value.filter((v) => v !== null && v !== undefined && v !== "") : [];
  };

  const [vendorNames, setVendorNames] = useState<string[]>(getFilterValue(filters, "businessPartnerName"));
  const [pan, setPan] = useState<string[]>(getFilterValue(filters, "pan"));
  // const [instamartGrnNumber, setInstamartGrnNumber] = useState<string[]>(getFilterValue(filters, "instamartGrnNumber"));
  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState<string[]>(
    getFilterValue(filters, "vendorInvoiceNumber")
  );
  const [commentsFilter, setCommentsFilter] = useState<string[]>(getFilterValue(filters, "newVendorComments"));
  const [instamartDnNumber, setInstamartDnNumber] = useState<string[]>(getFilterValue(filters, "instamartDnNumber"));
  const [ticketId, setTicketId] = useState<string[]>(getFilterValue(filters, "ticketId"));
  const [disputeStatus, setDisputeStatus] = useState<string[]>(getFilterValue(filters, "vendorDisputeStatus"));

  //  performance optimization
  // const [grnInput, setGrnInput] = useState("");
  const [panInput, setPanInput] = useState("");
  const [vendorNameInput, setVendorNameInput] = useState("");
  const [invoiceInput, setInvoiceInput] = useState("");
  const [instamartDNInput, setInstamartDNInput] = useState("");
  const [taskIDInput, setTaskIDInput] = useState("");
  // const debouncedGrnInput = useDebounce(grnInput, 200);
  const debouncedPanInput = useDebounce(panInput, 200);
  const debouncedVendorNameInput = useDebounce(vendorNameInput, 200);
  const debouncedInvoiceInput = useDebounce(invoiceInput, 200);
  const debouncedInstamartDNInput = useDebounce(instamartDNInput, 200);
  const debouncedTaskIDInput = useDebounce(taskIDInput, 200);

  const handleClose = () => {
    setOpenFilterOfDisputedEntries(false);
  };

  const handleApplyFilter = () => {
    const newFilters = [];

    if (vendorNames.length > 0) {
      newFilters.push({ id: "businessPartnerName", value: vendorNames });
    }

    if (pan.length > 0) {
      newFilters.push({ id: "pan", value: pan });
    }

    // if (instamartGrnNumber.length > 0) {
    //   newFilters.push({ id: "instamartGrnNumber", value: instamartGrnNumber });
    // }

    if (vendorInvoiceNumber.length > 0) {
      newFilters.push({ id: "vendorInvoiceNumber", value: vendorInvoiceNumber });
    }
    if (instamartDnNumber.length > 0) {
      newFilters.push({ id: "instamartDnNumber", value: instamartDnNumber });
    }
    if (ticketId.length > 0) {
      newFilters.push({ id: "ticketId", value: ticketId });
    }

    if (disputeStatus.length > 0) {
      newFilters.push({ id: "vendorDisputeStatus", value: disputeStatus });
    }

    //  in data newVendorComments is boolean

    if (commentsFilter[0] === "Unread") {
      newFilters.push({ id: "newVendorComments", value: ["Unread"] });
    } else if (commentsFilter[0] === "Read") {
      newFilters.push({ id: "newVendorComments", value: ["Read"] });
    }
    // commentsFilter[0] === "All" => skip filter

    // Apply filters to MRT
    setFilters(newFilters);
    // Apply filters to data
    // const filteredData = data.filter((item) => {
    //   const vendorNameMatch = vendorNames.length === 0 || vendorNames.includes(item.businessPartnerName);
    //   const panMatch = pan.length === 0 || pan.includes(item.pan);
    //   const grnMatch = instamartGrnNumber.length === 0 || instamartGrnNumber.includes(item.instamartGrnNumber);
    //   const invoiceMatch = vendorInvoiceNumber.length === 0 || vendorInvoiceNumber.includes(item.vendorInvoiceNumber);
    //   const dnMatch = instamartDnNumber.length === 0 || instamartDnNumber.includes(item.instamartDnNumber);
    //   const ticketIdMatch = ticketId.length === 0 || ticketId.includes(String(item.ticketId));
    //   const commentsMatch =
    //     commentsFilter[0] === "All" ||
    //     (commentsFilter[0] === "Unread" && item.newVendorComments) ||
    //     (commentsFilter[0] === "Read" && !item.newVendorComments);
    //   return vendorNameMatch && panMatch && grnMatch && invoiceMatch && dnMatch && ticketIdMatch && commentsMatch;
    // });
    // setDisputedEntriesData(filteredData);
    // setSummaryData(generateSummaryFromData(filteredData));

    handleClose();
  };

  const handleClearFilters = () => {
    setFilters([]);
    setVendorNames([]);
    setPan([]);
    // setInstamartGrnNumber([]);
    setVendorInvoiceNumber([]);
    setCommentsFilter(["All"]);
    setInstamartDnNumber([]);
    setTicketId([]);
    setDisputeStatus([]);

    handleClose();
  };

  function extractUniqueOptions<T extends string>(data: T[], field: string): { [key: string]: T }[] {
    // const values = [EXCLUDE, ...new Set(data.filter((val) => typeof val === "string" && val.trim() !== ""))];
    const values = [...new Set(data.filter((val) => typeof val === "string" && val.trim() !== ""))];
    return values.map((val) => ({ [field]: val } as { [key: string]: T }));
  }

  // const allGrnOptions = useMemo(() => {
  //   const allGrnOptionsValues = data
  //     .map((item) => item.instamartGrnNumber)
  //     .filter((val) => typeof val === "string" && val.trim() !== "");
  //   return extractUniqueOptions<string>(allGrnOptionsValues, "instamartGrnNumber");
  // }, [data]);

  const allPanOptions = useMemo(() => {
    const allPanOptionsValues = data
      .map((item) => item.pan)
      .filter((val) => typeof val === "string" && val.trim() !== "");
    return extractUniqueOptions<string>(allPanOptionsValues, "pan");
  }, [data]);

  const allVendorNameOptions = useMemo(() => {
    const allVendorNameOptionsValues = data
      .map((item) => item.businessPartnerName)
      .filter((val) => typeof val === "string" && val.trim() !== "");
    return extractUniqueOptions<string>(allVendorNameOptionsValues, "businessPartnerName");
  }, [data]);

  const allInvoiceOptions = useMemo(() => {
    const allInvoiceOptionsValues = data
      .map((item) => item.vendorInvoiceNumber)
      .filter((val) => typeof val === "string" && val.trim() !== "");
    return extractUniqueOptions<string>(allInvoiceOptionsValues, "vendorInvoiceNumber");
  }, [data]);

  const allInstamartDNOptions = useMemo(() => {
    const allInstamartDNOptionsValues = data
      .map((item) => item.instamartDnNumber)
      .filter((val) => typeof val === "string" && val.trim() !== "");
    return extractUniqueOptions<string>(allInstamartDNOptionsValues, "instamartDnNumber");
  }, [data]);

  const allTaskIDOptions = useMemo(() => {
    const allTaskIDOptionsValues = data
      .map((item) => item.ticketId)
      .filter((val) => val !== null && val !== undefined && val !== "")
      .map((val) => String(val));
    return extractUniqueOptions<string>(allTaskIDOptionsValues, "ticketId");
  }, [data]);

  // Dispute Status options from enum
  const allDisputeStatusOptions = useMemo(() => {
    return [
      { vendorDisputeStatus: VendorDisputeStatusEnumDR.NO_DISPUTE },
      { vendorDisputeStatus: VendorDisputeStatusEnumDR.DISPUTE_RAISED },
      { vendorDisputeStatus: VendorDisputeStatusEnumDR.DISPUTE_RESOLVED },
    ];
  }, []);

  // const grnNumberOptions = useMemo(() => {
  //   if (!debouncedGrnInput) return allGrnOptions.slice(0, 100);
  //   return allGrnOptions.filter((opt) =>
  //     opt.instamartGrnNumber.toLowerCase().includes(debouncedGrnInput.toLowerCase())
  //   );
  // }, [allGrnOptions, debouncedGrnInput]);

  const panOptions = useMemo(() => {
    if (!debouncedPanInput) return allPanOptions.slice(0, 100);
    return allPanOptions.filter((opt) => opt.pan.toLowerCase().includes(debouncedPanInput.toLowerCase()));
  }, [allPanOptions, debouncedPanInput]);

  const instamartDNNumberOptions = useMemo(() => {
    if (!debouncedInstamartDNInput) return allInstamartDNOptions.slice(0, 100);
    return allInstamartDNOptions.filter((opt) =>
      opt.instamartDnNumber.toLowerCase().includes(debouncedInstamartDNInput.toLowerCase())
    );
  }, [allInstamartDNOptions, debouncedInstamartDNInput]);

  const taskIDOptions = useMemo(() => {
    if (!debouncedTaskIDInput) return allTaskIDOptions.slice(0, 100);
    return allTaskIDOptions.filter((opt) => opt.ticketId.toLowerCase().includes(debouncedTaskIDInput.toLowerCase()));
  }, [allTaskIDOptions, debouncedTaskIDInput]);

  const vendorNameOptions = useMemo(() => {
    if (!debouncedVendorNameInput) return allVendorNameOptions.slice(0, 100);
    return allVendorNameOptions.filter((opt) =>
      opt.businessPartnerName.toLowerCase().includes(debouncedVendorNameInput.toLowerCase())
    );
  }, [allVendorNameOptions, debouncedVendorNameInput]);

  const invoiceNumberOptions = useMemo(() => {
    if (!debouncedInvoiceInput) return allInvoiceOptions.slice(0, 100);
    return allInvoiceOptions.filter((opt) =>
      opt.vendorInvoiceNumber.toLowerCase().includes(debouncedInvoiceInput.toLowerCase())
    );
  }, [allInvoiceOptions, debouncedInvoiceInput]);

  return (
    <div>
      <Dialog open={openFilterOfDisputedEntries} maxWidth={DIALOG_MAX_WIDTHS.SM} fullWidth>
        <DialogTitle className="dialog-title">
          <div className="heading-m">Filter</div>
          <div className="vertical_center_align">
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor Name</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={vendorNames}
                setTags={setVendorNames}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Vendor Name"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={vendorNameOptions}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={option.businessPartnerName === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}
                  >
                    {option.businessPartnerName}
                  </li>
                )}
                accessor={"businessPartnerName"}
                uniqueKeyAccessor={"businessPartnerName"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={vendorNameInput}
                onInputChange={(_, val) => setVendorNameInput?.(val)}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor PAN</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={pan}
                setTags={setPan}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select PAN Number"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={panOptions}
                renderOption={(props, option) => (
                  <li {...props} style={option.pan === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}>
                    {option.pan}
                  </li>
                )}
                accessor={"pan"}
                uniqueKeyAccessor={"pan"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={panInput}
                onInputChange={(_, val) => setPanInput?.(val)}
              />
            </Box>
          </Box>

          {/* <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Instamart GRN Number</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={instamartGrnNumber}
                setTags={setInstamartGrnNumber}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select GRN Number"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={grnNumberOptions}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={option.instamartGrnNumber === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}
                  >
                    {option.instamartGrnNumber}
                  </li>
                )}
                accessor={"instamartGrnNumber"}
                uniqueKeyAccessor={"instamartGrnNumber"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={grnInput}
                onInputChange={(_, val) => setGrnInput?.(val)}
              />
            </Box>
          </Box> */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor Invoice Number</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={vendorInvoiceNumber}
                setTags={setVendorInvoiceNumber}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Invoice Number"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={invoiceNumberOptions}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={option.vendorInvoiceNumber === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}
                  >
                    {option.vendorInvoiceNumber}
                  </li>
                )}
                accessor={"vendorInvoiceNumber"}
                uniqueKeyAccessor={"vendorInvoiceNumber"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={invoiceInput}
                onInputChange={(_, val) => setInvoiceInput?.(val)}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Instamart DN Number</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={instamartDnNumber}
                setTags={setInstamartDnNumber}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Instamart DN Number"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={instamartDNNumberOptions}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={option.instamartDnNumber === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}
                  >
                    {option.instamartDnNumber}
                  </li>
                )}
                accessor={"instamartDnNumber"}
                uniqueKeyAccessor={"instamartDnNumber"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={instamartDNInput}
                onInputChange={(_, val) => setInstamartDNInput?.(val)}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Task ID</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={ticketId}
                setTags={setTicketId}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Task ID"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={taskIDOptions}
                renderOption={(props, option) => (
                  <li {...props} style={option.ticketId === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}>
                    {option.ticketId}
                  </li>
                )}
                accessor={"ticketId"}
                uniqueKeyAccessor={"ticketId"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={taskIDInput}
                onInputChange={(_, val) => setTaskIDInput?.(val)}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Dispute Status</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={disputeStatus}
                setTags={setDisputeStatus}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Dispute Status"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={allDisputeStatusOptions}
                renderOption={(props, option) => <li {...props}>{option.vendorDisputeStatus}</li>}
                accessor={"vendorDisputeStatus"}
                uniqueKeyAccessor={"vendorDisputeStatus"}
                ListboxComponent={VirtualizedListboxComponent}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor Remarks</Typography>
            <Box sx={{ width: "350px" }}>
              <RadioGroup
                className="space_between"
                row
                defaultValue="All" // Default selected value
                value={commentsFilter[0]} // Controlled value
                onChange={(e) => {
                  console.log(e.target.value); // Handle the selected value (all, unread, or read)
                  setCommentsFilter([e.target.value]); // Update the state with the selected value
                }}
              >
                <FormControlLabel value="All" control={<Radio size="small" />} label="All" />
                <FormControlLabel value="Unread" control={<Radio size="small" />} label="Unread" />
                <FormControlLabel value="Read" control={<Radio size="small" />} label="Read" />
              </RadioGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions className="dialog-actions">
          <CustomButton size="medium" colorToken="violet" variant="text" onClick={handleClearFilters}>
            Clear Filter
          </CustomButton>
          <CustomButton
            size="medium"
            colorToken="violet"
            variant="contained"
            //    disabled={isEditing}
            //    startIcon={<LoadingIcon loading={isEditing} />}
            onClick={() => {
              handleApplyFilter();
            }}
          >
            Apply
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FilterOfDisputedEntries;
